import { FC, forwardRef, memo, useMemo } from 'react';

import clsx from 'clsx';

import { Loader } from '../loader/Loader';
import { ButtonStyled, useStyles } from './Button.styles';
import { ButtonColor, ButtonProps, ButtonVariant } from './Button.types';

/**
 * @component Button
 * @description Buttons are interactive elements that enable users to trigger actions or navigate to different views.
 * They provide visual cues about clickability and importance through different variants.
 *
 * @designSystem
 * - **Button Types**:
 *   - `filled`: Primary actions, most visually prominent (contained variant)
 *   - `outlined`: Secondary actions with medium emphasis
 *   - `text`: Tertiary actions with the least visual emphasis
 *   - `twoTone`: Special branded buttons for important actions
 *   - `outlinedError`: Used for destructive actions that require confirmation
 *
 * - **Button Sizes**:
 *   - `small`: Compact size for space-constrained areas
 *   - `medium`: Default size for most interfaces
 *   - `large`: Increased prominence for key actions
 *
 * - **States**:
 *   - Normal: Default interactive state
 *   - Disabled: Non-interactive state
 *   - Loading: Shows a loading indicator during asynchronous operations
 *
 * @usage
 * - Use only one primary (filled) button per section or container
 * - Position the primary action button on the right in a button group
 * - Use clear, concise, action-oriented labels (e.g., "Save" not "OK")
 * - Include loading states for asynchronous operations
 * - Maintain consistent spacing between buttons (minimum 8px)
 * - Use outlined buttons for secondary actions
 * - Use text buttons for tertiary or less important actions
 *
 * @accessibility
 * - Ensure adequate color contrast (4.5:1 for normal text, 3:1 for large text)
 * - Provide descriptive labels that clearly indicate the action
 * - Include appropriate ARIA attributes when used within forms
 * - Ensure buttons are keyboard accessible and have visible focus states
 *
 * @example
 * ```jsx
 * // Primary button
 * <Button buttonType="filled">Save</Button>
 *
 * // Secondary button
 * <Button buttonType="outlined">Cancel</Button>
 *
 * // Loading state
 * <Button buttonType="filled" isLoading>Processing</Button>
 *
 * // With icon
 * <Button buttonType="filled">Continue <ArrowForwardIcon /></Button>
 * ```
 */
export const Button: FC<ButtonProps> = memo(
  forwardRef(
    ({ buttonType, isLoading, children, color, size = 'medium', labelClasses, loaderColor, ...props }, ref) => {
      const styles = useStyles(size, isLoading);

      const { variant, calculatedColor } = useMemo(() => {
        let variant: ButtonVariant = undefined;
        let calculatedColor: ButtonColor = color;

        switch (buttonType) {
          case 'twoTone':
            break;

          case 'filled':
            variant = 'contained';
            calculatedColor = 'secondary';
            break;

          case 'outlined':
            variant = 'outlined';
            break;

          case 'outlinedError':
            variant = 'outlined';
            break;

          case 'text':
            variant = 'text';
            break;

          case 'filter':
          case 'filterFilled':
            variant = 'outlined';
            break;

          default:
            variant = 'text';
        }

        return { variant, calculatedColor };
      }, [buttonType, color]);

      return (
        <div className={clsx(styles.buttonContainer, props.className)}>
          <ButtonStyled
            {...props}
            ref={ref}
            variant={variant}
            type={props.type ?? 'button'}
            color={calculatedColor}
            size={size}
            disableRipple
            disableElevation
            classes={{
              ...props.classes,
              root: clsx(styles.root, props.classes?.root),
            }}
            className={clsx(
              buttonType === 'twoTone' && styles.twoTone,
              buttonType === 'outlinedError' && styles.outlinedError,
              buttonType === 'filter' && styles.filter,
              buttonType === 'filterFilled' && clsx(styles.filter, styles.filterFilled),
            )}
          >
            <div
              className={clsx(
                labelClasses,
                buttonType === 'twoTone' && styles.twoToneContent,
                buttonType === 'twoTone' && size === 'large' && styles.twoToneContentLarge,
                buttonType === 'twoTone' && size === 'small' && styles.twoToneContentSmall,
              )}
            >
              {children}
            </div>
          </ButtonStyled>

          {!!isLoading && (
            <div className={styles.loader}>
              <Loader size="small" customColor={loaderColor} />
            </div>
          )}
        </div>
      );
    },
  ),
);
